// telemetry/otel-logs.js
// Browser OTel log emitter.
// Imported statically — only uses @opentelemetry/api-logs and @opentelemetry/api,
// both of which are no-ops until a LoggerProvider is registered by logging-provider.js.
//
// Also owns the "pino firing" flag used by console-capture.js to avoid double-emit
// when Pino's write() calls console.* (transmit.send handles those via OTel instead).

import { logs, SeverityNumber } from '@opentelemetry/api-logs'
import { context } from '@opentelemetry/api'
import { serializeLogArgs } from './log-serializer'

// ------------------------------------------------------------------
// Pino firing flag
// Set to true by logger-utils.js consoleLogWriterConfig before each
// console.* call, so the console patch in console-capture.js can skip
// OTel emit for those (transmit.send handles them).
// ------------------------------------------------------------------
let _pinoFiring = false
export const setPinoFiring = (v) => {
  _pinoFiring = v
}
export const isPinoFiring = () => _pinoFiring

// ------------------------------------------------------------------
// Readiness + minimum level gate
// Set by logging-provider.js once the LoggerProvider is registered.
// ------------------------------------------------------------------
let _initialized = false
let _minLevelValue = 30 // warn by default

const LEVEL_VALUES = { fatal: 50, error: 40, warn: 30, info: 20, debug: 10 }

const LEVEL_TO_SEVERITY = {
  fatal: SeverityNumber.FATAL,
  error: SeverityNumber.ERROR,
  warn: SeverityNumber.WARN,
  info: SeverityNumber.INFO,
  debug: SeverityNumber.DEBUG,
}

export const setOtelLogsReady = (minLevel = 'warn') => {
  _initialized = true
  _minLevelValue = LEVEL_VALUES[minLevel] ?? LEVEL_VALUES.warn
}

// ------------------------------------------------------------------
// Request correlation
// Populated by request-context.js when it intercepts x-request-id from responses.
// ------------------------------------------------------------------
let _lastRequestId = null
export const setLastRequestId = (id) => {
  _lastRequestId = id || null
}
export const getLastRequestId = () => _lastRequestId

// ------------------------------------------------------------------
// Emitter
// ------------------------------------------------------------------

/**
 * Emit a structured browser OTel log record.
 *
 * @param {string}   level     - 'fatal' | 'error' | 'warn' | 'info' | 'debug'
 * @param {any[]}    args      - array of arguments (like console args)
 * @param {object}   [extraAttrs] - additional span attributes (e.g. log.source, log.module)
 */
export const emitBrowserOtelLog = (level, args, extraAttrs = {}) => {
  if (!_initialized) return
  const lvl = level || 'info'
  if ((LEVEL_VALUES[lvl] ?? LEVEL_VALUES.info) < _minLevelValue) return
  try {
    const argsArray = Array.isArray(args) ? args : [args]
    const { body, attributes: serializedAttrs } = serializeLogArgs(argsArray)

    const requestAttrs = {}
    if (_lastRequestId) {
      requestAttrs['http.request_id'] = _lastRequestId
      requestAttrs['chaos.request_id'] = _lastRequestId
    }

    logs.getLogger('chaos-player-frontend').emit({
      severityNumber: LEVEL_TO_SEVERITY[lvl] ?? SeverityNumber.INFO,
      severityText: lvl.toUpperCase(),
      body,
      attributes: {
        'log.origin': 'browser',
        'log.level': lvl,
        'browser.url': window.location?.href ?? '',
        'browser.user_agent': navigator?.userAgent ?? '',
        ...serializedAttrs,
        ...requestAttrs,
        ...extraAttrs,
      },
      context: context.active(),
    })
  } catch {
    // Never let OTel emission crash the app
  }
}
